/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Grid from "@mui/material/Grid";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Box, TextField, Button, MenuItem, FormControl } from "@mui/material";

import {
  ILeaveApplication,
  LeaveApplicationFormData
} from "../../../interfaces/LeaveApplicationInterface";

import { RootState } from "../../../store";
import Panel from "../../../components/Panel";
import { t, tl } from "../../../components/translate";
import { useLeaveTypes } from "../../../hooks/query/useLeaveTypes";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import { Employee, UserMode } from "../../../interfaces/User";
import { useHolidays } from "../../../hooks/query/useHolidays";
import { notificationAdd } from "../../../actions/notification";
import { useLeaveBalances } from "../../../hooks/query/useLeaveBalances";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import EmployeeAndSpSelect from "../../../components/Employee/EmployeeAndSpSelect";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";
import {
  bsFullDate,
  getFiscalYear
} from "../../../components/Calendar/functions/calendarFunctions";
import { patchLeaveApplication, postLeaveApplication } from "../../../api/leaveApplication";

// functions
function getDateRange(start: Date, end: Date): Date[] {
  const range: Date[] = [];
  const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (current <= last) {
    range.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return range;
}

interface LeaveApplicationCreateEditProps {
  onClose: () => void;
  leaveApplication?: Partial<ILeaveApplication> | null;
  createLeaveOnBehalfOfEmployee?: boolean;
  onSuccess?: (data?: ILeaveApplication) => void;
}

// eslint-disable-next-line import/prefer-default-export
export function LeaveApplicationCreateEdit({
  onClose,
  onSuccess,
  leaveApplication,
  createLeaveOnBehalfOfEmployee = false
}: LeaveApplicationCreateEditProps): React.ReactNode {
  const today = new Date();

  const isEdit = !!leaveApplication?.id;

  // hooks
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const { data: leavesTypes = [] } = useLeaveTypes();
  const { data: holidays = [] } = useHolidays({ fiscalYear: getFiscalYear(today) });

  // selectors
  const { user, userCreds } = useSelector((s: RootState) => s.userContext);

  // local states
  const [leaveDaysCount, setLeaveDaysCount] = React.useState<number>(0);
  const [deductibleDaysCount, setDeductibleDaysCount] = React.useState<number>(0);
  const [openConfirmationDialog, setOpenConfirmationDialog] = React.useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = React.useState<Employee | ServiceProvider | null>(
    leaveApplication?.employee || leaveApplication?.serviceProvider || null
  );

  // react query hook
  const { data: leaveBalances = [] } = useLeaveBalances({
    employeeIds:
      selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.Employee
        ? [selectedEmployee.id]
        : [],
    serviceProviderIds:
      selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.ServiceProvider
        ? [selectedEmployee.id]
        : [],
    enabled: !!selectedEmployee?.id
  });

  // form
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<LeaveApplicationFormData>({
    defaultValues: {
      leaveTypeId: leaveApplication?.leaveTypeId || undefined,
      startDate: leaveApplication?.startDate || new Date().toISOString(),
      endDate: leaveApplication?.endDate || new Date().toISOString(),
      reason: leaveApplication?.reason || "",
      employeeId: leaveApplication?.employeeId || undefined,
      serviceProviderId: leaveApplication?.serviceProviderId || undefined,
      resourceCentreId: rc?.id
    }
  });

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const leaveTypeId = watch("leaveTypeId");
  const selectedLeaveType = leavesTypes.find((l) => l.id === leaveTypeId);

  const onSubmit = async (values: LeaveApplicationFormData) => {
    if (
      !selectedEmployee?.id ||
      !rc?.id ||
      !selectedEmployee?.userAuthenticable?.[0]?.authenticable
    ) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          message: "User context is not properly set",
          autoTimeout: true
        })
      );
      return;
    }

    try {
      const payload: LeaveApplicationFormData = {
        ...values,
        leaveTypeId: Number(values.leaveTypeId),
        employeeId:
          selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.Employee
            ? selectedEmployee.id
            : undefined,
        serviceProviderId:
          selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.ServiceProvider
            ? selectedEmployee.id
            : undefined
      };

      let response;
      if (isEdit && leaveApplication?.id) {
        response = await patchLeaveApplication({ id: leaveApplication.id, data: payload });
      } else {
        response = await postLeaveApplication(payload);
      }

      const { success, message, data: newApplication } = response;

      if (success) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            message:
              message ||
              (isEdit
                ? "Application updated successfully!"
                : "Application submitted successfully!"),
            autoTimeout: true
          })
        );
        onSuccess?.(newApplication);
        onClose();
      }
      setOpenConfirmationDialog(false);
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message:
            err?.message ||
            err?.data?.message ||
            "Failed to submit leave application. Please try again.",
          autoTimeout: true
        })
      );
      setOpenConfirmationDialog(false);
    }
  };

  // memo
  const leaveOptions = React.useMemo(
    () =>
      leavesTypes.map((leave) => {
        const balanceObj = leaveBalances.find(
          (b) =>
            Number(b.leaveTypeId) === Number(leave.id) &&
            (Number(b.employeeId) === Number(selectedEmployee?.id) ||
              Number(b.serviceProviderId) === Number(selectedEmployee?.id))
        );

        const availableBalance = balanceObj?.balance || 0;
        const isDisabled = leave.isPaid && availableBalance <= 0;

        return {
          ...leave,
          availableBalance,
          isDisabled
        };
      }),
    [leavesTypes, leaveBalances, selectedEmployee?.id]
  );

  // use effects
  React.useEffect(() => {
    if (!createLeaveOnBehalfOfEmployee && user && userCreds) {
      // TODO: have userContext.user contain userAutheticable
      setSelectedEmployee({ ...user, userAuthenticable: userCreds.userAuthenticables });
    }
  }, [createLeaveOnBehalfOfEmployee, user, userCreds]);

  React.useEffect(() => {
    if (!selectedEmployee) return;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = getDateRange(start, end);

    // Calculate total days in the range
    const total = days.length;
    setLeaveDaysCount(total);

    // Calculate actual leave days (excluding only holidays from the holidays array)
    const leaveDays = days.filter((d) => {
      const isHoliday = holidays.some((h) => h.date.slice(0, 10) === d.toISOString().slice(0, 10));
      const isWeeklyOff = selectedEmployee?.defaultWeeklyOffs?.includes(d.getDay());

      return !isHoliday && !isWeeklyOff;
    }).length;

    setDeductibleDaysCount(leaveDays);
  }, [
    startDate,
    endDate,
    holidays,
    setDeductibleDaysCount,
    selectedEmployee?.defaultWeeklyOffs,
    selectedEmployee
  ]);

  return (
    <Panel
      onClose={onClose}
      title={t("leaveApplication.apply")}
      footer={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-end",
            gap: 2
          }}
        >
          <Button onClick={onClose} sx={{ height: "32px", width: "60px" }}>
            {tl("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenConfirmationDialog(true)}
            disabled={isSubmitting}
            sx={{ height: "35px", width: "80px" }}
          >
            {isEdit ? tl("update") : tl("create")}
          </Button>
        </Box>
      }
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto"
        }}
      >
        {openConfirmationDialog && (
          <OkhatiDialog
            title="Confirm Leave Application"
            description={`${leaveDaysCount} days of leave will be applied from ${bsFullDate(
              new Date(startDate)
            )} to ${bsFullDate(
              new Date(endDate)
            )}. ${deductibleDaysCount} days will be deducted from your ${
              selectedLeaveType?.name || "selected"
            } leave credit upon approval. Are you sure you want to submit this leave application?`}
            open={openConfirmationDialog}
            next={handleSubmit(onSubmit)}
            cancel={() => setOpenConfirmationDialog(false)}
            readMode={false}
          />
        )}

        <form id="leave-application-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <EmployeeAndSpSelect
                TextFieldProps={{ required: true, fullWidth: true }}
                disabled={!createLeaveOnBehalfOfEmployee}
                selectedEmployee={selectedEmployee}
                onChange={(emp) => {
                  setSelectedEmployee(emp);
                }}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="leaveTypeId"
                control={control}
                rules={{ required: "Leave type is required" }}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.leaveTypeId}>
                    <TextField
                      {...field}
                      label="Leave Type"
                      value={field.value || ""}
                      select
                      size="small"
                      error={!!errors.leaveTypeId}
                      variant="outlined"
                      placeholder="Select Leave Type"
                      slotProps={{ inputLabel: { shrink: true } }}
                    >
                      {leaveOptions.map((leave) => (
                        <MenuItem key={leave.id} value={leave.id} disabled={leave.isDisabled}>
                          {leave.name} {leave.isPaid ? `(${leave.availableBalance})` : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="startDate"
                control={control}
                rules={{ required: "Start date is required" }}
                render={({ field }) => (
                  <CalendarDropdown
                    fullwidth
                    TextFieldProps={{ variant: "outlined" }}
                    label="Start Date"
                    placeholder="Select Start Date"
                    date={new Date(field.value)}
                    onChange={(val) => {
                      field.onChange(val.toISOString());
                      // If startDate > endDate, set endDate = startDate
                      if (new Date(val) > new Date(endDate)) {
                        setValue("endDate", val.toISOString());
                      }
                    }}
                    showHolidayInRed
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="endDate"
                control={control}
                rules={{ required: "End date is required" }}
                render={({ field }) => (
                  <CalendarDropdown
                    fullwidth
                    TextFieldProps={{ variant: "outlined" }}
                    label="End Date"
                    placeholder="Select End Date"
                    date={new Date(field.value)}
                    onChange={(val) => field.onChange(val.toISOString())}
                    minValidDate={new Date(startDate)}
                    showHolidayInRed
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="reason"
                control={control}
                rules={{ required: "Reason is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Reason for Leave"
                    multiline
                    rows={4}
                    placeholder="Please provide a detailed reason for your leave application"
                    onFocus={(event) => event.target.select()}
                    error={!!errors.reason}
                    helperText={errors.reason?.message}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </Panel>
  );
}
